import moment from "moment";

import { CROP_TYPE_FCPO, CropItemData, FieldPracticeResponse, PRACTICE_SLUG, PracticeFCPO, PracticeTillage, WithInjectedName, ZERO_UUID } from "models";

import {
  GROUP_TYPE,
  PRACTICE_TYPE,
  TillageItemData,
  TillageValue,
  TimelineCalendarItemsProps,
} from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";
import { determinePracticeEndDate, groupPracticeItemsByFourWeeks } from "pages/app/field-timeline/utils/PracticeUtils.ts";

const createItems = (
  groupedTillageItems: Map<number, PracticeFCPO[]>,
  tillageTypes: Array<WithInjectedName<PracticeTillage>>,
  groupType: GROUP_TYPE,
  baselineYears: number[],
  isMandatory: boolean
): TimelineCalendarItemsProps<TillageItemData>[] => {
  const tillageItems: TimelineCalendarItemsProps<TillageItemData>[] = [];
  groupedTillageItems.forEach((value) => {
    const tillageValues: TillageValue[] = [];
    value.forEach((practice) => {
      if (practice.assignedPracticeId === ZERO_UUID) return;

      tillageValues.push({
        name: tillageTypes.find((tillageType) => tillageType.id === practice.assignedPracticeId)?.name ?? "Unknown",
        startDate: practice.startDate,
        endDate: practice.endDate,
        type: practice.type,
        payout: practice.payout,
        isMandatory: isMandatory,
        isBaseline: baselineYears.includes(practice.year!),
        isConfirmed: practice.isConfirmed,
      });
    });

    if (!tillageValues) return;

    tillageItems.push({
      id: `${PRACTICE_TYPE.TILLAGE}-${value[0].id}`,
      title: "",
      group: groupType,
      start_time: moment(value[0].endDate),
      end_time: moment(value[0].endDate).add(5, "months"),
      canResize: false,
      canMove: false,
      cropItemId: value[0].cropId,
      practiceType: PRACTICE_TYPE.TILLAGE,
      data: {
        tillageValues: tillageValues,
      },
    });
  });

  return tillageItems;
};

export const createTillageItems = (
  fieldCropOverViewResponse: FieldPracticeResponse,
  cropItems: TimelineCalendarItemsProps<CropItemData>[],
  tillageTypes: Array<WithInjectedName<PracticeTillage>>,
  baselineYears: number[],
  isMandatory: boolean
): TimelineCalendarItemsProps<TillageItemData>[] => {
  let mainCropTillageItems: TimelineCalendarItemsProps<TillageItemData>[] = [];
  let sideCropTillageItems: TimelineCalendarItemsProps<TillageItemData>[] = [];

  const tillageList = fieldCropOverViewResponse.practices.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES);

  const mainCropTillageList = tillageList.filter((tillageItem) => {
    const cropType = fieldCropOverViewResponse.crops.find((crop) => crop.id === tillageItem.cropId)?.cropType;
    if (!cropType) {
      return true;
    }
    return cropType === CROP_TYPE_FCPO.MAIN;
  });

  const sideCropTillageList = tillageList.filter((tillageItem) => {
    const cropType = fieldCropOverViewResponse.crops.find((crop) => crop.id === tillageItem.cropId)?.cropType;

    return cropType === CROP_TYPE_FCPO.UNDERSOWING || cropType === CROP_TYPE_FCPO.COVER;
  });

  if (mainCropTillageList.length) {
    const groupedMainCropTillageItems = groupPracticeItemsByFourWeeks(mainCropTillageList);
    const tillageItems = createItems(groupedMainCropTillageItems, tillageTypes, GROUP_TYPE.MAIN_CROP_TILLAGE, baselineYears, isMandatory);
    // Update the end time of each tillage practice to the end of the main crop or the next tillage practice, if available.
    mainCropTillageItems = tillageItems.map((tillageItem) => {
      tillageItem.end_time = determinePracticeEndDate(
        tillageItem,
        tillageItems,
        cropItems.filter((cropItem) => cropItem.practiceType === PRACTICE_TYPE.MAIN_CROP)
      );
      return tillageItem;
    });
  }

  if (sideCropTillageList.length) {
    const groupedSideCropTillageItems = groupPracticeItemsByFourWeeks(sideCropTillageList);
    const tillageItems = createItems(groupedSideCropTillageItems, tillageTypes, GROUP_TYPE.SIDE_CROP_TILLAGE, baselineYears, isMandatory);
    // Update the end time of each tillage practice to the end of the main crop or the next tillage practice, if available.
    sideCropTillageItems = tillageItems.map((tillageItem) => {
      tillageItem.end_time = determinePracticeEndDate(
        tillageItem,
        tillageItems,
        cropItems.filter((cropItem) => cropItem.practiceType === PRACTICE_TYPE.UNDERSOWING_CROP || cropItem.practiceType === PRACTICE_TYPE.COVER_CROP)
      );
      return tillageItem;
    });
  }

  return [...mainCropTillageItems, ...sideCropTillageItems];
};
